/** TEAM_USERS control: configure the onboarding team members (email + names + optional
 *  phone + a per-row access group) and create their dashboard accounts.
 *
 *  "Edit team" opens a row-editor (AppDialog) that saves the list via save_team_members
 *  (config.onboarding.team_members). "Create accounts" runs create_accounts, which creates
 *  a User + Tour_Operator_User per member and adds each to its chosen access group
 *  (idempotent — members whose account already exists are skipped). Reads the current
 *  members (with an `exists` flag) + available access groups from item.detail.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { teamMemberActions, type TenantCtx } from '../tenantLinks';

interface MemberRow {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  access_group_code: string;
  exists?: boolean;
}
interface AgOption {
  code: string;
  name: string;
}

function parseMembers(detail: Record<string, unknown>): MemberRow[] {
  const raw = Array.isArray(detail.team_members) ? detail.team_members : [];
  return raw.map((r) => {
    const o = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
    return {
      email: String(o.email ?? ''),
      first_name: String(o.first_name ?? ''),
      last_name: String(o.last_name ?? ''),
      phone: String(o.phone ?? ''),
      access_group_code: String(o.access_group_code ?? ''),
      exists: o.exists === true,
    };
  });
}

function parseAgs(detail: Record<string, unknown>): AgOption[] {
  const raw = Array.isArray(detail.available_access_groups) ? detail.available_access_groups : [];
  const out: AgOption[] = [];
  for (const a of raw) {
    const o = (a && typeof a === 'object' ? a : {}) as Record<string, unknown>;
    if (typeof o.code === 'string' && o.code) out.push({ code: o.code, name: String(o.name ?? o.code) });
  }
  return out;
}

export function TeamUsersControl({
  item,
  topId,
  toCode,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const ctx: TenantCtx = { id: topId, code: toCode };
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<MemberRow[]>([]);
  const ags = parseAgs(item.detail);
  const current = parseMembers(item.detail);
  const createdCount = current.filter((m) => m.exists).length;

  const save = useMutation({
    mutationFn: (payload: MemberRow[]) => items.saveTeamMembers(topId, item.id, payload),
    onSuccess: () => {
      snackbar.show('Team members saved.', 'success');
      setOpen(false);
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const create = useMutation({
    mutationFn: () => items.createAccounts(topId, item.id),
    onSuccess: (res) => {
      const failed = !res.run || res.run.status === 'error';
      snackbar.show(
        failed ? res.run?.error_message || 'Account creation failed.' : 'Accounts created.',
        failed ? 'error' : 'success',
      );
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Account creation failed.', 'error'),
  });

  const setRow = (i: number, patch: Partial<MemberRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { email: '', first_name: '', last_name: '', phone: '', access_group_code: ags[0]?.code ?? '' },
    ]);
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i));

  const valid = rows.every((r) => r.email.trim() !== '' && r.email.includes('@'));

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Team members: {current.length || 'none'}
          {current.length > 0 ? ` (${createdCount} created)` : ''}
        </Typography>
        {canManage && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setRows(parseMembers(item.detail));
                setOpen(true);
              }}
            >
              Edit team
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={current.length === 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              Create accounts
            </Button>
          </>
        )}
      </Stack>
      {current.length > 0 && (
        <Stack spacing={0.25}>
          {current.map((m) => (
            <Stack key={m.email} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {m.first_name} {m.last_name} —{' '}
                {/* The payload has no user id, so the staff grid's email filter
                    is how the created account is reached. */}
                <ValueActions actions={teamMemberActions(m.email, ctx)}>{m.email}</ValueActions>
                {m.access_group_code ? ` · ${m.access_group_code}` : ''}
              </Typography>
              <Chip
                size="small"
                label={m.exists ? 'account exists' : 'not created'}
                color={m.exists ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
          ))}
        </Stack>
      )}

      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Team members"
        size="md"
        actions={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!valid || save.isPending} onClick={() => save.mutate(rows)}>
              Save
            </Button>
          </>
        }
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell sx={{ width: 120 }}>First name</TableCell>
              <TableCell sx={{ width: 120 }}>Last name</TableCell>
              <TableCell sx={{ width: 130 }}>Phone</TableCell>
              <TableCell sx={{ width: 160 }}>Access group</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    type="email"
                    value={r.email}
                    onChange={(e) => setRow(i, { email: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.first_name}
                    onChange={(e) => setRow(i, { first_name: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.last_name}
                    onChange={(e) => setRow(i, { last_name: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.phone}
                    onChange={(e) => setRow(i, { phone: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.access_group_code}
                    onChange={(e) => setRow(i, { access_group_code: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {ags.map((a) => (
                      <MenuItem key={a.code} value={a.code}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <IconButton size="small" aria-label="Remove" onClick={() => removeRow(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button size="small" sx={{ mt: 1 }} onClick={addRow}>
          Add member
        </Button>
        {!valid && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Every row needs a valid email address.
          </Typography>
        )}
      </AppDialog>
    </Box>
  );
}
